import { FormPageType } from 'mk2/constants';
import { UploadedPhoto } from 'mk2/containers/PhotosUpload/PhotosUpload';
import {
    PHOTO_UPLOAD_FAILURE,
    PHOTO_UPLOAD_PROGRESS,
    PHOTO_UPLOAD_REMOVE,
    PHOTO_UPLOAD_REMOVE_ALL,
    PHOTO_UPLOAD_REQUEST,
    PHOTO_UPLOAD_ROTATE,
    PHOTO_UPLOAD_SUCCESS,
    PhotoUploadFailureAction,
    PhotoUploadProgressAction,
    PhotoUploadRemoveAction,
    PhotoUploadRemoveAllAction,
    PhotoUploadRequestAction,
    PhotoUploadRotateAction,
    PhotoUploadSuccessAction,
} from 'mk2/containers/PhotosUpload/PhotosUpload.actions';
import {
    MK_REDUX_FORM_CANCEL_PAGE,
    MK_REDUX_FORM_CLOSE_PAGE,
    MK_REDUX_FORM_OPEN_PAGE,
    MKReduxFormCancelPage,
    MKReduxFormClosePage,
    MKReduxFormOpenPage,
} from 'mk2/helpers/form.actions';
import { tupdate } from 'mk2/helpers/types';
import { PhotoEntity, PhotoOrientation } from 'mk2/schemas';
import { Reducer } from 'redux';
import { FormAction as ReduxFormAction, FormState as ReduxFormState, reducer as reduxFormReducer } from 'redux-form';

export enum PhotoUploadStatus {
    PENDING,
    SUCCESS,
    FAILURE,
}

interface PhotoUploadBase {
    id: number;
    fileName: string;
    fileSize: number;
}

export interface PendingPhotoUpload extends PhotoUploadBase {
    status: PhotoUploadStatus.PENDING;
    progress: number;
}

export interface SuccessfulPhotoUpload extends PhotoUploadBase {
    status: PhotoUploadStatus.SUCCESS;
    fileName: string;
    fileSize: number;
    photoId: number;
    orientation: PhotoOrientation;
}

export type PhotoEntityOrSuccessfulPhotoUpload = PhotoEntity | SuccessfulPhotoUpload;

export interface FailedPhotoUpload extends PhotoUploadBase {
    status: PhotoUploadStatus.FAILURE;
    errorMessage: string;
}

export interface MKReduxFormState extends ReduxFormState {
    currentFormPage: FormPageType;  // current page of multi-page form
    previousValuesOfMainPage: any;  // values when we left the main page and opened other subpage
    photos: UploadedPhoto[];
    values?: {
        photos?: Array<PendingPhotoUpload | SuccessfulPhotoUpload | FailedPhotoUpload>,
    };
}

export const initialMKReduxFormState: MKReduxFormState = {
    currentFormPage: FormPageType.Main,
    previousValuesOfMainPage: null,
    photos: [],

    // Potrebujeme kvoli zdedenym typingom mat deklarovane aj registeredFields.
    //
    // Ako defaultnu hodnotu nastavime undefined, rovnaku hodnotu pouziva aj redux-form.
    // Pri prvej REGISTER_FIELD action si potom undefined zameni za object.
    //
    // Povodne sme pouzivali ako defaultnu hodnotu []. To sposobovalo, ze pri submite
    // sa niektore form fields s errorom neoznacili ako 'touched=true'. Preto sa pri nich
    // potom nevyrenderovali cervene boxy s chybovou spravou.
    registeredFields: undefined,
};

export interface MKReduxFormsState {
    [formName: string]: MKReduxFormState;
}

const initialMKReduxFormsState: MKReduxFormsState = {};

type MKReduxFormsReducerActions =
    MKReduxFormOpenPage
    | MKReduxFormClosePage
    | MKReduxFormCancelPage
    | PhotoUploadRequestAction
    | PhotoUploadProgressAction
    | PhotoUploadSuccessAction
    | PhotoUploadFailureAction
    | PhotoUploadRotateAction
    | PhotoUploadRemoveAction
    | PhotoUploadRemoveAllAction
    | any;

function replaceOrAppend<S>(arr: S[], x: S, isIdent: (x1: S, x2: S) => boolean): S[] {
    let replaced = false;

    const ret: S[] = [];
    for (const a of arr) {
        if (isIdent(a, x)) {
            ret.push(x);
            replaced = true;
        } else {
            ret.push(a);
        }
    }

    if (!replaced) {
        ret.push(x);
    }

    return ret;
}

function hasPhotosField(state: MKReduxFormState): boolean {
    // podla declaracie registeredFields je pole, ale ked debugujem je to objekt
    return state.registeredFields && 'photos' in state.registeredFields;
}

function initializeUndefinedParams(
    actualState: MKReduxFormState, initialState: MKReduxFormState, ensurePhotosExists: boolean,
): MKReduxFormState {
    const patchedState: MKReduxFormState = tupdate(initialState, actualState);

    // patched state je uz urcite novy objekt, mozem priamo modifikovat ten
    if (ensurePhotosExists) {
        if (!patchedState.values) {
            patchedState.values = {
                photos: [],
            };
        } else if (!patchedState.values.photos) {
            patchedState.values.photos = [];
        }
    }

    return patchedState;
}

export const mkReduxFormsReducer: Reducer<MKReduxFormsState> =
    (state = initialMKReduxFormsState, action: MKReduxFormsReducerActions) => {

    switch (action.type) {
        case MK_REDUX_FORM_OPEN_PAGE: {
            const formName = action.formName;
            const formState = initializeUndefinedParams(state[formName], initialMKReduxFormState, false);

            return tupdate(state, {
                [formName]: tupdate(formState, {
                    currentFormPage: action.formPage,
                    previousValuesOfMainPage:
                        formState.currentFormPage === FormPageType.Main
                            ? formState.values // save current state of main page
                            : formState.previousValuesOfMainPage, // leaving to another subpage, keep what we have
                }),
            });
        }

        case MK_REDUX_FORM_CLOSE_PAGE: {
            const formName = action.formName;
            const formState = initializeUndefinedParams(state[formName], initialMKReduxFormState, false);

            // closing subpage via 'Confirm', commit changes
            return tupdate(state, {
                [formName]: tupdate(formState, {
                    currentFormPage: FormPageType.Main,
                    previousValuesOfMainPage: null,
                }),
            });
        }

        case MK_REDUX_FORM_CANCEL_PAGE: {
            const formName = action.formName;
            const formState = initializeUndefinedParams(state[formName], initialMKReduxFormState, false);

            // closing subpage via 'Cancel' button, rollback changes
            return tupdate(state, {
                [formName]: tupdate(formState, {
                    currentFormPage: FormPageType.Main,
                    values: formState.previousValuesOfMainPage,
                    previousValuesOfMainPage: null,
                }),
            });
        }

        case PHOTO_UPLOAD_REQUEST: {
            const formName = action.formName;

            // ignore for forms without photos field
            if (!hasPhotosField(state[formName])) {
                return state;
            }

            const formState = initializeUndefinedParams(state[formName], initialMKReduxFormState, true);

            const pu: PendingPhotoUpload = {
                status: PhotoUploadStatus.PENDING,
                id: action.uploadId,
                fileName: action.file.name,
                fileSize: action.file.size,
                progress: 0,
            };

            return tupdate(state, {
                [formName]: tupdate(formState, {
                    values: tupdate(formState.values, {
                        photos: replaceOrAppend(formState.values.photos, pu, (u1, u2) => u1.id === u2.id),
                    }),
                }),
            });
        }

        case PHOTO_UPLOAD_PROGRESS: {
            const formName = action.formName;

            // ignore for forms without photos field
            if (!hasPhotosField(state[formName])) {
                return state;
            }

            const formState = initializeUndefinedParams(state[formName], initialMKReduxFormState, true);

            let pu: PendingPhotoUpload = formState.values.photos.find(
                (u) => u.status === PhotoUploadStatus.PENDING && u.id === action.uploadId,
            ) as PendingPhotoUpload;

            if (pu) {
                pu = tupdate(pu, {progress: action.progress});

                return tupdate(state, {
                    [formName]: tupdate(formState, {
                        values: tupdate(formState.values, {
                            photos: replaceOrAppend(formState.values.photos, pu, (u1, u2) => u1.id === u2.id),
                        }),
                    }),
                });
            }
            // if pending photoupload not found (cancelled multipage form page)
            return state;
        }

        case PHOTO_UPLOAD_SUCCESS: {
            const formName = action.formName;

            // ignore for forms without photos field
            if (!hasPhotosField(state[formName])) {
                return state;
            }

            const formState = initializeUndefinedParams(state[formName], initialMKReduxFormState, true);

            const pu: PendingPhotoUpload = formState.values.photos.find(
                (u) => u.status === PhotoUploadStatus.PENDING && u.id === action.uploadId,
            ) as PendingPhotoUpload;

            if (pu) {

                const su: PhotoEntityOrSuccessfulPhotoUpload = {
                    ...action.response.entities.photo[action.response.result.photo],
                    status: PhotoUploadStatus.SUCCESS,
                    id: action.uploadId,
                    fileName: pu.fileName,
                    fileSize: pu.fileSize,
                    photoId: action.response.result.photo,
                    orientation: PhotoOrientation.NORMAL,
                };

                return tupdate(state, {
                    [formName]: tupdate(formState, {
                        values: tupdate(formState.values, {
                            photos: replaceOrAppend(formState.values.photos, su as SuccessfulPhotoUpload, (u1, u2) => u1.id === u2.id),
                        }),
                    }),
                });
            }
            // if pending photoupload not found (cancelled multipage form page)
            return state;
        }

        case PHOTO_UPLOAD_FAILURE: {
            const formName = action.formName;

            // ignore for forms without photos field
            if (!hasPhotosField(state[formName])) {
                return state;
            }

            const formState = initializeUndefinedParams(state[formName], initialMKReduxFormState, true);

            const pu: PendingPhotoUpload = formState.values.photos.find(
                (u) => u.status === PhotoUploadStatus.PENDING && u.id === action.uploadId,
            ) as PendingPhotoUpload;

            if (pu) {
                const su: FailedPhotoUpload = {
                    status: PhotoUploadStatus.FAILURE,
                    id: action.uploadId,
                    fileName: pu.fileName,
                    fileSize: pu.fileSize,
                    errorMessage: action.error,
                };

                return tupdate(state, {
                    [formName]: tupdate(formState, {
                        values: tupdate(formState.values, {
                            photos: replaceOrAppend(formState.values.photos, su, (u1, u2) => u1.id === u2.id),
                        }),
                    }),
                });
            }
            // if pending photoupload not found (cancelled multipage form page)
            // or coulde be failure action generated from xhr.abort() and
            // upload was already removed by previous PHOTO_UPLOAD_REMOVE
            return state;
        }

        case PHOTO_UPLOAD_ROTATE: {
            const formName = action.formName;

            // ignore for forms without photos field
            if (!hasPhotosField(state[formName])) {
                return state;
            }

            const formState = initializeUndefinedParams(state[formName], initialMKReduxFormState, true);

            let su: SuccessfulPhotoUpload = formState.values.photos.find(
                (u) => u.status === PhotoUploadStatus.SUCCESS && u.id === action.uploadId,
            ) as SuccessfulPhotoUpload;
            if (!su) {
                throw new Error('Photo is not successfully uploaded');
            }

            su = tupdate(su, {orientation: action.orientation});

            return tupdate(state, {
                [formName]: tupdate(formState, {
                    values: tupdate(formState.values, {
                        photos: replaceOrAppend(formState.values.photos, su, (u1, u2) => u1.id === u2.id),
                    }),
                }),
            });
        }

        case PHOTO_UPLOAD_REMOVE: {
            const formName = action.formName;

            // ignore for forms without photos field
            if (!hasPhotosField(state[formName])) {
                return state;
            }

            const formState = initializeUndefinedParams(state[formName], initialMKReduxFormState, true);

            return tupdate(state, {
                [formName]: tupdate(formState, {
                    values: tupdate(formState.values, {
                        photos: formState.values.photos.filter((u) => u.id !== action.uploadId),
                    }),
                }),
            });
        }

        case PHOTO_UPLOAD_REMOVE_ALL: {
            const formName = action.formName;

            // ignore for forms without photos field
            if (!hasPhotosField(state[formName])) {
                return state;
            }

            const formState = initializeUndefinedParams(state[formName], initialMKReduxFormState, true);

            return tupdate(state, {
                [formName]: tupdate(formState, {
                    values: tupdate(formState.values, {
                        photos: [],
                    }),
                }),
            });
        }

        default: {
            // pass to default redux-form reducer
            const rfstate = reduxFormReducer(state, action) as MKReduxFormsState;
            if (!rfstate) {
                return rfstate;
            }

            // ak redux form zalozil pre aktualny form novy state, injectni do stavu
            // ze current form page je 'main'
            const rfaction = action as ReduxFormAction;
            if (rfaction.type.startsWith('@@redux-form/')) {
                const formName = rfaction.meta ? rfaction.meta.form : null;
                if (formName && rfstate[formName] && !rfstate[formName].currentFormPage) {
                    rfstate[formName].currentFormPage = FormPageType.Main;
                }
            }

            return rfstate;
        }
    }

};
